<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4">
    <div style="max-width:640px">
      <h3>Bordbooking</h3>
      <p class="mt-3">
        Gjestene booker bord på nett – velger antall, dato og klokkeslett og får bekreftelse på e-post og/eller SMS.
        Bookingene ser du under <a href="/bookings">Bordbookinger</a>. Hvilke dager og tider som kan
        bookes styres av <a href="/openinghours">åpningstidene</a> og <a href="/closingdays">stengte dager</a>.
      </p>

      <div v-if="hasMultiLocations()" class="select-location-box mt-4">
        <span class="ingredient-title">Velg avdeling</span>
        <select class="form-select" v-model="locationId">
          <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
        </select>
      </div>

      <div v-if="settings" class="mt-4">
        <!-- Booking is dine-in only: the «Book bord» button sits in the storefront's «Vil du spise her
             eller ta med?» dialog, which only a location with dine-in (QR) shows. -->
        <div v-if="location && !location.offerQR" class="alert alert-warning">
          Denne avdelingen tilbyr ikke «spise her» i nettbutikken, så «Book bord»-knappen vises ikke der.
          Bordbooking er bare for gjester som skal spise her – slå på «Tilbyr QR-bestilling» under
          <a :href="'/locationEdit?locationId=' + locationId">Avdelinger</a> først.
        </div>
        <div class="bo_form-switch-row">
          <div class="form-switch-wrapper">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="settings.enabled" />
                <i></i>
                <span class="bold ms-2">Ta imot bordbooking på nett</span>
              </div>
            </label>
          </div>
        </div>

        <div class="mt-3 p-3 border rounded bg-body-tertiary">
          <label class="form-label fw-bold">Lenke til bordbooking</label>
          <p class="text-muted small mb-2">
            Vises som «Book bord» i nettbutikken når den er slått på. Legg lenken på egen nettside,
            Google-profil og Facebook/Instagram.
          </p>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <code class="user-select-all bo_booking-url">{{ settings.bookingUrl }}</code>
            <button type="button" class="bo_btn_secondary" @click="copyUrl">{{ copied ? 'Kopiert' : 'Kopier' }}</button>
          </div>
        </div>

        <div class='bo_hr mt-4'></div>
        <h5 class="mt-4">Kapasitet</h5>

        <div class="mb-3">
          <label class="form-label fw-bold">Sitteplasser for nettbooking</label>
          <p class="text-muted small">
            Hvor mange gjester som kan sitte samtidig via nettbooking. Sett lavere enn hele lokalet om
            dere vil holde av bord til drop-in.
            <span v-if="settings.suggestedSeats > 0">
              Bordene dine har til sammen {{ settings.suggestedSeats }} plasser
              (<a href="#" @click.prevent="settings.seats = settings.suggestedSeats">bruk {{ settings.suggestedSeats }}</a>).
            </span>
            <span v-else>Tips: legg inn bordene under <a href="/tables">Bord</a>, så foreslår vi et tall her.</span>
          </p>
          <input type="number" class="form-control" style="max-width:160px" min="0" max="1000" v-model.number="settings.seats" />
          <p v-if="settings.enabled && settings.seats < 1" class="text-danger small mt-1 mb-0">
            Med 0 plasser finner gjestene aldri et ledig bord.
          </p>
        </div>

        <div class="mb-3">
          <label class="form-label fw-bold">Største booking på nett</label>
          <p class="text-muted small">Større bookinger enn dette kan ikke gjøres på nett – gjesten får beskjed om å ringe restauranten.</p>
          <input type="number" class="form-control" style="max-width:160px" min="1" max="100" v-model.number="settings.maxPartySize" />
        </div>

        <div class='bo_hr mt-4'></div>
        <h5 class="mt-4">Tider</h5>

        <div class="row g-3">
          <div class="col-sm-6">
            <label class="form-label fw-bold">Tidsintervall</label>
            <select class="form-select" v-model.number="settings.slotMinutes">
              <option :value="15">Hvert 15. minutt</option>
              <option :value="30">Hver halvtime</option>
            </select>
          </div>
          <div class="col-sm-6">
            <label class="form-label fw-bold">Bordet holdes av i</label>
            <select class="form-select" v-model.number="settings.durationMinutes">
              <option v-for="m in [60, 90, 120, 150, 180, 240]" :key="m" :value="m">{{ formatMinutes(m) }}</option>
            </select>
          </div>
          <div class="col-sm-6">
            <label class="form-label fw-bold">Siste bordsetting før stengetid</label>
            <select class="form-select" v-model.number="settings.lastSeatingMinutesBeforeClose">
              <option v-for="m in [0, 30, 60, 90, 120]" :key="m" :value="m">{{ m === 0 ? 'Fram til stengetid' : formatMinutes(m) + ' før' }}</option>
            </select>
          </div>
          <div class="col-sm-6">
            <label class="form-label fw-bold">Tidligst booking</label>
            <select class="form-select" v-model.number="settings.leadTimeMinutes">
              <option :value="0">Med en gang</option>
              <option :value="30">30 minutter før</option>
              <option :value="60">1 time før</option>
              <option :value="120">2 timer før</option>
              <option :value="240">4 timer før</option>
              <option :value="1440">Dagen før</option>
            </select>
          </div>
          <div class="col-sm-6">
            <label class="form-label fw-bold">Kan bookes fram i tid</label>
            <select class="form-select" v-model.number="settings.maxDaysAhead">
              <option v-for="d in [14, 30, 60, 90, 180]" :key="d" :value="d">{{ d }} dager</option>
            </select>
          </div>
        </div>

        <div class='bo_hr mt-4'></div>
        <h5 class="mt-4">Bekreftelse og varsling</h5>

        <div class="bo_form-switch-row">
          <div class="form-switch-wrapper">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="settings.autoConfirm" />
                <i></i>
                <span class="bold ms-2">Bekreft bookinger automatisk</span>
              </div>
            </label>
          </div>
        </div>
        <p class="text-muted small mt-2">
          <span v-if="settings.autoConfirm">Gjesten får bekreftelse med en gang.</span>
          <span v-else>Bookingen venter til dere bekrefter den under Bordbookinger. Gjesten får beskjed når det skjer – og holder plassen imens.</span>
        </p>

        <h6 class="mt-4">Beskjed til gjesten</h6>
        <p class="text-muted small">
          Gjesten får beskjed når bordet er booket, bekreftet eller avbestilt. Velg hvordan.
          E-post er gratis; <strong>SMS koster kr 1 per melding</strong> og faktureres dere. Én booking
          kan gi 1–3 meldinger.
        </p>
        <div class="bo_form-switch-row">
          <div class="form-switch-wrapper">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="settings.guestEmail" />
                <i></i>
                <span class="bold ms-2">E-post til gjesten</span>
                <span class="ms-2 badge bg-success">gratis</span>
              </div>
            </label>
          </div>
        </div>
        <div class="bo_form-switch-row mt-2">
          <div class="form-switch-wrapper">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="settings.guestSms" />
                <i></i>
                <span class="bold ms-2">SMS til gjesten</span>
                <span class="ms-2 badge bg-warning text-dark">kr 1 per SMS</span>
              </div>
            </label>
          </div>
        </div>
        <p v-if="!settings.guestSms && !settings.guestEmail" class="text-danger small mt-2 mb-0">
          Gjesten må få beskjed på minst én måte – ellers har de verken bekreftelse eller lenke for å avbestille.
        </p>

        <h6 class="mt-4">Beskjed til dere</h6>
        <p class="text-muted small">
          Vil dere ha beskjed om hver nye booking fra nettet? Skriv inn e-postadressen og/eller
          mobilnummeret varselet skal gå til. Et tomt felt betyr at dere ikke får varsel på e-post eller SMS.
        </p>
        <div class="row g-3">
          <div class="col-sm-6">
            <label class="form-label fw-bold">På e-post <span class="badge bg-success ms-1">gratis</span></label>
            <input type="email" class="form-control" placeholder="post@restauranten.no" v-model.trim="settings.notifyEmail" />
          </div>
          <div class="col-sm-6">
            <label class="form-label fw-bold">På SMS <span class="badge bg-warning text-dark ms-1">kr 1 per SMS</span></label>
            <input type="tel" class="form-control" placeholder="Mobilnummer" v-model.trim="settings.notifyPhone" />
          </div>
        </div>

        <div class='bo_hr mt-4'></div>
        <h5 class="mt-4">Informasjon til gjestene</h5>
        <p class="text-muted small">Vises øverst på bookingssiden. F.eks. «Større booking enn 8? Ring oss.» eller «Bordet holdes i 15 minutter etter booket tid.»</p>
        <textarea class="form-control" rows="3" maxlength="500" v-model.trim="settings.infoText"></textarea>

        <div class="mt-4 mb-5">
          <button type="button" class="bo_btn" :disabled="saving || (!settings.guestSms && !settings.guestEmail)" @click="save">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
          <span v-if="saved" class="ms-3 text-success">Lagret</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import BookingService from "@/services/booking/BookingService";
import LocationService from "@/services/location/LocationService";
import LocationUtil from "@/util/LocationUtil";
import MerchantUtil from "@/util/MerchantUtil";

export default {
  name: 'BookingSettings',
  components: {LoadingSpinner},
  data() {
    return {
      loading: true,
      saving: false,
      saved: false,
      copied: false,
      locationId: '',
      locations: [],
      settings: null
    };
  },
  computed: {
    location() {
      return this.locations.find(l => l.id === this.locationId) || null;
    }
  },
  watch: {
    locationId() {
      this.loadSettings();
    }
  },
  mounted() {
    LocationService.listLocations().then(
        (response) => {
          this.locations = response.data;
          const fromQuery = this.$route.query.locationId;
          this.locationId = fromQuery || (this.locations.length ? this.locations[0].id : '');
          if (!this.locationId) {
            this.loading = false;
          }
        },
        (error) => {
          console.log(error);
          this.loading = false;
        }
    );
  },
  methods: {
    hasMultiLocations() {
      return LocationUtil.hasMultiLocations();
    },
    formatMinutes(minutes) {
      if (minutes < 60) return minutes + ' min';
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      return rest === 0 ? hours + (hours === 1 ? ' time' : ' timer') : hours + ' t ' + rest + ' min';
    },
    loadSettings() {
      this.loading = true;
      this.saved = false;
      BookingService.getSettings(this.locationId).then(
          (response) => {
            this.settings = response.data;
            this.loading = false;
          },
          (error) => {
            console.log(error);
            this.loading = false;
          }
      );
    },
    save() {
      this.saving = true;
      this.saved = false;
      BookingService.saveSettings(this.locationId, this.settings).then(
          (response) => {
            this.settings = response.data;
            this.saving = false;
            this.saved = true;
            // The «Bordbookinger» menu entry follows location.tableBooking.enabled, cached at login.
            MerchantUtil.refreshLocations();
          },
          (error) => {
            console.log(error);
            this.saving = false;
          }
      );
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.settings.bookingUrl);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (e) {
        console.log(e);
      }
    }
  }
};
</script>

<style scoped>
/* A URL has no spaces to wrap at; without this it widens the whole page on a phone. */
.bo_booking-url {
  overflow-wrap: anywhere;
  word-break: break-all;
}
</style>
